import { useState, useRef, useEffect, useCallback } from 'react';
import { findNearestConnectionPoint, createThemedElementFactory, getEdgePoint } from '../types/elements';
import { deepClone, centerTextOnArrow } from '../utils/canvasUtils';
import { getTextAnchor } from '../utils/elementUtils';
import { isErdArrowElement, rerouteErdElements } from '../utils/erdParser';
import { useTheme } from './useTheme';

/**
 * Обновляет координаты всех стрелок, подключенных к указанным элементам
 * @param {Array} elements - все элементы
 * @param {Set|Array} movedElementIds - ID перемещённых элементов
 * @returns {Array} - обновлённые элементы
 */
export const updateConnectedArrows = (elements, movedElementIds) => {
    const movedIds = movedElementIds instanceof Set ? movedElementIds : new Set(movedElementIds);

    const elementMap = new Map();
    elements.forEach(el => elementMap.set(el.id, el));

    // Pass 1: update arrow endpoints for connected elements; track which arrows changed
    const updatedArrowIds = new Set();
    const withArrows = elements.map(el => {
        if (el.type !== 'arrow') return el;

        let updated = { ...el };
        let needsUpdate = false;

        if (el.startConnection && movedIds.has(el.startConnection.elementId)) {
            const connected = elementMap.get(el.startConnection.elementId);
            if (connected) {
                const pt = getEdgePoint(connected, el.startConnection.side);
                updated.x = pt.x;
                updated.y = pt.y;
                needsUpdate = true;
            }
        }

        if (el.endConnection && movedIds.has(el.endConnection.elementId)) {
            const connected = elementMap.get(el.endConnection.elementId);
            if (connected) {
                const pt = getEdgePoint(connected, el.endConnection.side);
                updated.x2 = pt.x;
                updated.y2 = pt.y;
                needsUpdate = true;
            }
        }

        if (needsUpdate && updated.pathPoints && updated.pathPoints.length >= 2) {
            const pts = [...updated.pathPoints];
            pts[0] = { x: updated.x, y: updated.y };
            pts[pts.length - 1] = { x: updated.x2, y: updated.y2 };
            updated.pathPoints = pts;
        }

        if (needsUpdate) updatedArrowIds.add(el.id);
        return needsUpdate ? updated : el;
    });

    if (updatedArrowIds.size === 0) return withArrows;

    const hasMovedErdArrow = withArrows.some(
        (el) => el.type === 'arrow' && updatedArrowIds.has(el.id) && isErdArrowElement(el)
    );
    const routedElements = hasMovedErdArrow ? rerouteErdElements(withArrows) : withArrows;

    // Pass 2: reposition any text labels attached to arrows that just moved
    const arrowMap = new Map();
    routedElements.forEach(el => { if (el.type === 'arrow') arrowMap.set(el.id, el); });

    return routedElements.map(el => {
        if (el.type === 'text' && el.attachedToArrow && updatedArrowIds.has(el.attachedToArrow)) {
            const arrow = arrowMap.get(el.attachedToArrow);
            if (arrow) {
                const pos = centerTextOnArrow(arrow);
                return { ...el, x: pos.x, y: pos.y };
            }
        }
        return el;
    });
};

// Основной хук для управления элементами Canvas
export const useCanvasElements = (assignZ, incrementZCounter, recordHistoryFn) => {
    const { theme } = useTheme();
    const [elements, setElements] = useState([]);
    const [editingTextElement, setEditingTextElement] = useState(null);

    // Создаем тематизированный ElementFactory
    const ThemedElementFactory = createThemedElementFactory(theme);

    // Референс для отслеживания множественного перетаскивания
    const multiDragRef = useRef({
        isDragging: false,
        startMouseX: 0,
        startMouseY: 0,
        originals: {}
    });

    // Создаем обертку для recordHistory, которая передает текущие элементы
    const recordHistory = (label = 'Изменение') => {
        recordHistoryFn(elements, label);
    };

    // Удаление выбранных элементов
    const deleteSelectedElements = (selectedElementIds, selectedElementId, clearSelection, snapshotBefore) => {
        const ids = selectedElementIds.size
            ? Array.from(selectedElementIds)
            : selectedElementId
                ? [selectedElementId]
                : [];
        if (!ids.length) return;

        snapshotBefore(elements);
        setElements((prev) => prev.filter((el) => !ids.includes(el.id)));
        clearSelection();
        setTimeout(() => recordHistory('Удаление'), 0);
    };

    // Обработка перетаскивания элемента
    const handleElementDrag = (id, x, y, dragInProgressRef, snapshotBeforeActionRef, lastActionTypeRef) => {
        dragInProgressRef.current = true;
        if (!snapshotBeforeActionRef.current) {
            snapshotBeforeActionRef.current = deepClone(elements);
        }
        lastActionTypeRef.current = 'move';
        setElements((prev) => {
            // Если перетаскивается текст, прикреплённый к фигуре (например C4-подпись),
            // перенаправляем перемещение на родительский элемент.
            // x, y здесь — новый ЦЕНТР текстового элемента.
            const dragged = prev.find((e) => e.id === id);
            if (dragged?.type === 'text' && dragged.attachedToElement) {
                const parent = prev.find((e) => e.id === dragged.attachedToElement);
                if (parent) {
                    const parentNewX = parent.type === 'circle' ? x - parent.radius : x - (parent.width || 0) / 2;
                    const parentNewY = parent.type === 'circle' ? y - parent.radius : y - (parent.height || 0) / 2;
                    let next = prev.map((el) => (el.id === parent.id ? { ...el, x: parentNewX, y: parentNewY } : el));
                    next = updateConnectedArrows(next, [parent.id]);
                    return next.map((el) => (el.type === 'text' && el.attachedToElement === parent.id ? { ...el, x, y } : el));
                }
            }

            // Перемещаем сам элемент
            let next = prev.map((el) => (el.id === id ? { ...el, x, y } : el));

            // Обновляем связанные стрелки
            next = updateConnectedArrows(next, [id]);

            // Если это фигура, центрируем прикреплённый текст
            const parent = next.find((e) => e.id === id);
            if (parent && (parent.type === 'rectangle' || parent.type === 'circle')) {
                const { cx, cy } = getTextAnchor(parent);
                return next.map((el) => (el.type === 'text' && el.attachedToElement === id ? { ...el, x: cx, y: cy } : el));
            }
            return next;
        });
    };

    // Обработка множественного перетаскивания
    const handleElementDragMultiple = (id, x, y, selectedElementIds, dragInProgressRef, snapshotBeforeActionRef, lastActionTypeRef) => {
        // Если выделено несколько элементов И текущий элемент среди них
        if (selectedElementIds.size > 1 && selectedElementIds.has(id)) {
            if (!multiDragRef.current.isDragging) {
                const originals = {};
                elements.forEach((el) => {
                    if (selectedElementIds.has(el.id)) originals[el.id] = deepClone(el);
                });
                multiDragRef.current = {
                    isDragging: true,
                    startMouseX: x,
                    startMouseY: y,
                    originals
                };
                snapshotBeforeActionRef.current = deepClone(elements);
                lastActionTypeRef.current = 'move';
                return;
            }

            dragInProgressRef.current = true;
            const { startMouseX, startMouseY, originals } = multiDragRef.current;
            const dx = x - startMouseX;
            const dy = y - startMouseY;

            setElements((prev) => {
                // Сначала двигаем выбранные элементы (кроме стрелок со связями - они обновятся автоматически)
                let moved = prev.map((el) => {
                    if (!selectedElementIds.has(el.id)) return el;
                    const orig = originals[el.id];
                    if (!orig) return el;

                    // Стрелки со связями не двигаем вручную - они обновятся через updateConnectedArrows
                    if (el.type === 'arrow') {
                        // Если стрелка имеет связи с перемещаемыми блоками - пропускаем
                        const hasStartConnection = el.startConnection && selectedElementIds.has(el.startConnection.elementId);
                        const hasEndConnection = el.endConnection && selectedElementIds.has(el.endConnection.elementId);

                        if (hasStartConnection || hasEndConnection) {
                            // Двигаем только свободные концы
                            let updated = { ...el };
                            if (!hasStartConnection) {
                                updated.x = orig.x + dx;
                                updated.y = orig.y + dy;
                            }
                            if (!hasEndConnection) {
                                updated.x2 = orig.x2 + dx;
                                updated.y2 = orig.y2 + dy;
                            }
                            return updated;
                        }

                        // Свободная стрелка - двигаем полностью
                        const pts = el.pathPoints?.map((p) => ({ x: p.x + dx, y: p.y + dy })) || null;
                        return { ...el, x: orig.x + dx, y: orig.y + dy, x2: orig.x2 + dx, y2: orig.y2 + dy, pathPoints: pts };
                    }
                    return { ...el, x: orig.x + dx, y: orig.y + dy };
                });

                // Обновляем связанные стрелки для всех перемещённых блоков
                moved = updateConnectedArrows(moved, selectedElementIds);

                // Затем центрируем тексты, прикрепленные к фигурам, которые были перемещены
                const centers = new Map();
                moved.forEach((el) => {
                    if (selectedElementIds.has(el.id) && (el.type === 'rectangle' || el.type === 'circle')) {
                        centers.set(el.id, getTextAnchor(el));
                    }
                });

                return moved.map((el) => {
                    if (el.type === 'text' && el.attachedToElement) {
                        if (centers.has(el.attachedToElement)) {
                            // Родитель тоже перемещается — позиционируем текст в его новый центр
                            const { cx, cy } = centers.get(el.attachedToElement);
                            return { ...el, x: cx, y: cy };
                        }
                        // Родитель не перемещается — текст должен оставаться у родителя
                        const parent = moved.find((p) => p.id === el.attachedToElement);
                        if (parent) {
                            const { cx: pcx, cy: pcy } = getTextAnchor(parent);
                            return { ...el, x: pcx, y: pcy };
                        }
                    }
                    return el;
                });
            });
        } else {
            // Для одиночного элемента или элемента не в выделении используем обычный drag
            handleElementDrag(id, x, y, dragInProgressRef, snapshotBeforeActionRef, lastActionTypeRef);
        }
    };

    // Обработка двойного клика на элементе
    const handleElementDoubleClick = (id) => {
        const el = elements.find((e) => e.id === id);
        if (!el) return;

        if (el.type === 'text') {
            setEditingTextElement(id);
            return;
        }

        if (el.type === 'arrow') {
            const existing = elements.find(
                (e) => e.type === 'text' && e.attachedToArrow === id
            );
            if (existing) {
                setEditingTextElement(existing.id);
                return;
            }

            const pos = centerTextOnArrow(el);
            const t = assignZ(ThemedElementFactory.text(pos.x, pos.y, ''));
            t.attachedToArrow = id;
            incrementZCounter();
            setElements((p) => [...p, t]);
            recordHistory('Текст стрелки');
            setEditingTextElement(t.id);
            return;
        }

        if (el.type === 'rectangle' || el.type === 'circle') {
            const existing = elements.find(
                (e) => e.type === 'text' && e.attachedToElement === id
            );
            if (existing) {
                setEditingTextElement(existing.id);
                return;
            }

            let tx, ty;
            if (el.type === 'rectangle') {
                tx = el.x + el.width / 2;
                ty = el.y + el.height / 2;
            } else {
                tx = el.x + el.radius;
                ty = el.y + el.radius;
            }

            const t = assignZ(ThemedElementFactory.text(tx, ty, ''));
            t.attachedToElement = id;
            incrementZCounter();
            setElements((p) => [...p, t]);
            recordHistory('Текст фигуры');
            setEditingTextElement(t.id);
        }
    };

    // Завершение редактирования текста
    // autoSize=true: grow parent element to fit the new text (never shrink)
    const handleTextEditComplete = (id, payload, autoSize = false) => {
        setElements((prev) => {
            let updated = prev.map((el) => (el.id === id ? { ...el, ...payload } : el));

            if (autoSize) {
                const textEl = updated.find((el) => el.id === id);
                const parentEl = textEl?.attachedToElement
                    ? updated.find((el) => el.id === textEl.attachedToElement)
                    : null;
                if (textEl?.attachedToElement && !parentEl?.c4?.kind) {
                    const content = payload.content ?? textEl.content ?? '';
                    const lines = content.split('\n');
                    const fontSize = payload.fontSize ?? textEl.fontSize ?? 14;
                    const padding = 32;
                    const maxLen = Math.max(...lines.map((l) => l.length), 1);
                    const requiredW = Math.round(maxLen * fontSize * 0.6 + padding * 2);
                    const requiredH = Math.round(lines.length * fontSize * 1.7 + padding * 2);

                    updated = updated.map((el) => {
                        if (el.id !== textEl.attachedToElement) return el;
                        const newW = Math.max(el.width || 0, requiredW);
                        const newH = Math.max(el.height || 0, requiredH);
                        return { ...el, width: newW, height: newH };
                    });

                    // Re-center text on the (potentially resized) parent
                    const parent = updated.find((el) => el.id === textEl.attachedToElement);
                    if (parent) {
                        const cx = parent.x + (parent.width || 0) / 2;
                        const cy = parent.y + (parent.height || 0) / 2;
                        updated = updated.map((el) => (el.id === id ? { ...el, x: cx, y: cy } : el));
                    }
                }
            }

            return updated;
        });
        setEditingTextElement(null);
        recordHistory('Редактирование текста');
    };

    const handleTextEditCancel = () => setEditingTextElement(null);

    // Редактирование стрелки
    const handleArrowEdit = (arrowId, isStart, x, y, dragInProgressRef, snapshotBeforeActionRef, lastActionTypeRef) => {
        dragInProgressRef.current = true;
        if (!snapshotBeforeActionRef.current) {
            snapshotBeforeActionRef.current = deepClone(elements);
        }
        lastActionTypeRef.current = 'arrow-edit';

        setElements((prev) =>
            prev
                .map((el) => {
                    if (el.id !== arrowId) return el;
                    const conn = findNearestConnectionPoint(x, y, prev, el);

                    if (isStart) {
                        // Используем getEdgePoint для точного позиционирования на границе
                        const edgePoint = conn ? getEdgePoint(conn.element, conn.point.side) : null;
                        return {
                            ...el,
                            x: edgePoint ? edgePoint.x : x,
                            y: edgePoint ? edgePoint.y : y,
                            startConnection: conn
                                ? { elementId: conn.element.id, side: conn.point.side }
                                : null
                        };
                    }
                    // Используем getEdgePoint для точного позиционирования на границе
                    const edgePoint = conn ? getEdgePoint(conn.element, conn.point.side) : null;
                    return {
                        ...el,
                        x2: edgePoint ? edgePoint.x : x,
                        y2: edgePoint ? edgePoint.y : y,
                        endConnection: conn
                            ? { elementId: conn.element.id, side: conn.point.side }
                            : null
                    };
                })
                .map((el) => {
                    if (el.type === 'text' && el.attachedToArrow === arrowId) {
                        const a = prev.find((a) => a.id === arrowId);
                        if (a) {
                            const pos = centerTextOnArrow(a);
                            return { ...el, x: pos.x, y: pos.y };
                        }
                    }
                    return el;
                })
        );
    };

    // Изменение размера элемента
    const handleElementResize = (id, props) => {
        setElements((prev) => {
            let next = prev.map((el) => (el.id === id ? { ...el, ...props } : el));

            // Обновляем связанные стрелки
            next = updateConnectedArrows(next, [id]);

            const parent = next.find((e) => e.id === id);
            if (parent && (parent.type === 'rectangle' || parent.type === 'circle')) {
                const { cx, cy } = getTextAnchor(parent);
                return next.map((el) => (el.type === 'text' && el.attachedToElement === id ? { ...el, x: cx, y: cy } : el));
            }
            return next;
        });
    };

    // Генерация новых элементов
    const handleGenerateElements = (generatedElements) => {
        if (!generatedElements || !generatedElements.length) return;
        const elementsWithZ = generatedElements.map((el) => assignZ(el));
        // Заменяем существующие элементы новой диаграммой
        setElements(elementsWithZ);
        recordHistory('Генерация новой диаграммы');
    };

    // Добавление одного элемента
    const addElement = (element) => {
        setElements((prev) => {
            if (Array.isArray(element)) {
                return [...prev, ...element];
            }
            return [...prev, element];
        });
    };

    // Сброс состояния множественного перетаскивания
    const resetMultiDrag = () => {
        multiDragRef.current = {
            isDragging: false,
            startMouseX: 0,
            startMouseY: 0,
            originals: {}
        };
    };

    return {
        elements,
        setElements,
        editingTextElement,
        deleteSelectedElements,
        handleElementDrag,
        handleElementDragMultiple,
        handleElementDoubleClick,
        handleTextEditComplete,
        handleTextEditCancel,
        handleArrowEdit,
        handleElementResize,
        handleGenerateElements,
        addElement,
        resetMultiDrag,
        multiDragRef
    };
};
